import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { LayoutDashboard, RefreshCw } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import { canImpersonate } from '../../constants/adminRoles';
import { getAdminDashboardSummary } from '../../services/admin/dashboardService';
import { classifyAdminCallableError, formatAdminDate } from './adminUserDisplay';
import AdminKpiCards from './AdminKpiCards';
import AdminActionCenter from './AdminActionCenter';

export default function AdminHomePage() {
  const { t, language } = useLanguage();
  const { adminRole } = useAuth();
  const role = adminRole || 'super_admin';
  const [status, setStatus] = useState('loading');
  const [errorKind, setErrorKind] = useState(null);
  const [summary, setSummary] = useState(null);

  const load = useCallback(async () => {
    setStatus('loading');
    setErrorKind(null);
    try {
      const data = await getAdminDashboardSummary();
      setSummary(data);
      setStatus('ready');
    } catch (err) {
      setSummary(null);
      setErrorKind(classifyAdminCallableError(err));
      setStatus('error');
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const errorMessage =
    errorKind === 'unavailable'
      ? t('admin.home.error.unavailable')
      : errorKind === 'internal'
        ? t('admin.home.error.internal')
        : t('admin.home.error');

  const updatedLabel = t('admin.home.updatedAt').replace(
    '{date}',
    formatAdminDate(summary?.generatedAt, language),
  );

  return (
    <div className="mx-auto max-w-5xl space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-start gap-2">
          <LayoutDashboard className="mt-0.5 h-5 w-5 shrink-0 text-indigo-600" aria-hidden="true" />
          <div>
            <h1 className="text-xl font-semibold text-slate-900">{t('admin.home.title')}</h1>
            <p className="mt-1 text-sm text-slate-600">{t('admin.home.subtitle')}</p>
            {status === 'ready' ? (
              <p className="mt-1 text-xs text-slate-500">{updatedLabel}</p>
            ) : null}
          </div>
        </div>
        <button
          type="button"
          onClick={load}
          disabled={status === 'loading'}
          className="inline-flex items-center gap-1 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-60"
        >
          <RefreshCw className="h-4 w-4" aria-hidden="true" />
          {t('admin.home.refresh')}
        </button>
      </div>

      {status === 'loading' ? (
        <p className="text-sm text-slate-500" aria-live="polite">
          {t('admin.home.loading')}
        </p>
      ) : null}

      {status === 'error' ? (
        <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          <p>{errorMessage}</p>
          <button
            type="button"
            onClick={load}
            className="mt-2 rounded-lg border border-red-300 bg-white px-3 py-1.5 text-sm font-semibold text-red-800 hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-red-500"
          >
            {t('admin.home.retry')}
          </button>
        </div>
      ) : null}

      {status === 'ready' && summary ? (
        <>
          <AdminKpiCards kpis={summary.kpis} t={t} />
          <AdminActionCenter actions={summary.actions} role={role} t={t} />
        </>
      ) : null}

      {canImpersonate(role) ? (
        <section>
          <h2 className="text-sm font-semibold text-slate-800">{t('admin.home.quickAccess')}</h2>
          <div className="mt-3 grid gap-3 sm:grid-cols-2">
            <Link
              to="/admin/impersonate"
              className="rounded-xl border border-slate-200 bg-white p-4 text-sm font-medium hover:border-indigo-300 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              {t('admin.nav.impersonate')}
            </Link>
          </div>
        </section>
      ) : null}
    </div>
  );
}
